import React from 'react';
import dayjs from 'dayjs';

/**
 * Explains a seat count that does not match the visible roster.
 *
 * HEADCOUNT AND SEATS ARE DELIBERATELY NOT DERIVED FROM EACH OTHER, and this
 * is not a bug to reconcile:
 *
 *   - Headcount (people covered) EXCLUDES archived enrollees, retained or not.
 *   - Seat/slot math INCLUDES archived enrollees whose slot was retained.
 *
 * So "8 people, 10 seats used, 0 remaining" is a correct render. An enrollee
 * who used care in the current cycle keeps burning their seat after being
 * archived, until the cycle rolls over. Do not "fix" the mismatch by computing
 * one number from the other — you would be deleting the only signal the
 * operator has for why they cannot add a replacement.
 *
 * Renders nothing when there is nothing to explain, so it is safe to drop in
 * next to any seat figure whether or not that endpoint carries the field yet.
 */
export function retainedSlotsText(source) {
  const count = Number(
    source?.retained_by_archived
    ?? source?.slots?.retained_by_archived
    ?? 0,
  );
  if (!Number.isFinite(count) || count <= 0) return null;

  const raw = source?.retained_slots_return_on ?? source?.slots?.retained_slots_return_on ?? null;
  const on = raw ? dayjs(raw) : null;
  const seats = `${count} seat${count === 1 ? '' : 's'}`;

  // The dateless variant is a real path, not a defensive one: a policy whose
  // cycle resolves through the expiry-date fallback has no trustworthy return
  // date. It must still read as a sentence, never as a blank or "null".
  return on && on.isValid()
    ? `${seats} held by archived enrollees until ${on.format('DD MMM YYYY')}`
    : `${seats} held by archived enrollees until renewal`;
}

export function RetainedSlotsNote({ source, className = 'listing-card__fact' }) {
  const text = retainedSlotsText(source);
  if (!text) return null;
  return <span className={className}>{text}</span>;
}

export default RetainedSlotsNote;
